import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import { Box, Button, Typography } from '@mui/material';
import { FC, memo, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  ProductCourseDay,
  ProductCourseGroup,
  ProductCourseSection,
  ProductCoursesResponse,
} from 'src/entities/ProductCourse.entity';

import style from './print.module.css';

export const ROWS_PER_PAGE = 50;

type Props = {
  data: ProductCoursesResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  hasMissingParams?: boolean;
  issueDate: string;
  issueTime: string;
};

type FlatRow =
  | { kind: 'group'; group: ProductCourseGroup }
  | { kind: 'line'; group: ProductCourseGroup; lineIndex: number };

type RawPage = {
  day: ProductCourseDay;
  section: ProductCourseSection;
  rows: FlatRow[];
};

export type PageInfo = RawPage & { pageInSection: number; totalInSection: number };

function buildGroupRows(group: ProductCourseGroup): FlatRow[] {
  return [
    { kind: 'group', group },
    ...group.lines.map<FlatRow>((_, i) => ({ kind: 'line', group, lineIndex: i })),
  ];
}

export function buildPages(days: ProductCourseDay[]): RawPage[] {
  const pages: RawPage[] = [];
  days.forEach((day) => {
    day.sections.forEach((section) => {
      if (section.groups.length === 0) return;
      let currentRows: FlatRow[] = [];
      const flushPage = () => {
        if (currentRows.length === 0) return;
        pages.push({ day, section, rows: currentRows });
        currentRows = [];
      };
      section.groups.forEach((group) => {
        const groupRows = buildGroupRows(group);
        if (groupRows.length > ROWS_PER_PAGE) {
          // グループ単独で 1 ページに収まらない: 現在ページを締めて、グループ内で強制改ページ
          flushPage();
          for (let i = 0; i < groupRows.length; i += ROWS_PER_PAGE) {
            currentRows = groupRows.slice(i, i + ROWS_PER_PAGE);
            flushPage();
          }
          return;
        }
        if (currentRows.length + groupRows.length > ROWS_PER_PAGE) {
          flushPage();
        }
        currentRows.push(...groupRows);
      });
      flushPage();
    });
  });
  return pages;
}

// 和式区切り（全角空白 U+3000）は eslint no-irregular-whitespace を避けるため Unicode escape を使う
const IDEOGRAPHIC_SPACE = '\u3000';

export function formatJapaneseDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${y}年${m}月${d}日${IDEOGRAPHIC_SPACE}配送`;
}

function withPageNumbers(pages: RawPage[]): PageInfo[] {
  const totalByKey = new Map<string, number>();
  pages.forEach((p) => {
    const key = `${p.day.delivery_date}__${p.section.shipper_name}`;
    totalByKey.set(key, (totalByKey.get(key) ?? 0) + 1);
  });
  const indexByKey = new Map<string, number>();
  return pages.map((p) => {
    const key = `${p.day.delivery_date}__${p.section.shipper_name}`;
    const idx = (indexByKey.get(key) ?? 0) + 1;
    indexByKey.set(key, idx);
    return { ...p, pageInSection: idx, totalInSection: totalByKey.get(key) ?? 1 };
  });
}

function packingSummary(group: ProductCourseGroup): string {
  const entries = group.packing_breakdown.filter((e) => e.quantity > 0);
  if (entries.length === 0) return '';
  if (entries.length === 1) return entries[0].packing_style;
  return entries.map((e) => `${e.packing_style} ${e.quantity}`).join(' / ');
}

function formatCourseLabel(line: { course_number: number | null; rotation: number }, showRotation: boolean): string {
  if (line.course_number == null) return '';
  const num = String(line.course_number);
  if (showRotation && line.rotation >= 2) return `${num}-${line.rotation}`;
  return num;
}

const ProductCourseReportPresenter: FC<Props> = memo(({ data, isLoading, isError, hasMissingParams = false, issueDate, issueTime }) => {
  const rawPages = useMemo(() => buildPages(data?.days ?? []), [data]);
  const pages = useMemo(() => withPageNumbers(rawPages), [rawPages]);
  const totalAll = Math.max(pages.length, 1);

  return (
    <>
      <Helmet>
        <title>商品別コースリスト</title>
      </Helmet>
      <div className={style.shell} role="region" aria-label="商品別コースリスト プレビュー">
        <div className={style.toolbar}>
          <Button variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={() => window.close()}>
            戻る
          </Button>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            商品別コースリスト
          </Typography>
          <Box className={style.toolbarRight}>
            <Typography variant="caption" color="text.secondary">
              全 {totalAll} ページ
            </Typography>
            <Button variant="contained" size="small" startIcon={<PrintIcon />} onClick={() => window.print()}>
              印刷
            </Button>
          </Box>
        </div>

        <div className={style.body}>
          {hasMissingParams && (
            <div className={style.empty}>印刷データのパラメータが不足しています</div>
          )}
          {!hasMissingParams && isLoading && <div className={style.empty}>読み込み中...</div>}
          {!hasMissingParams && isError && <div className={style.empty}>データの取得に失敗しました。</div>}
          {!hasMissingParams && !isLoading && !isError && pages.length === 0 && (
            <div className={style.empty}>該当するデータはありません。</div>
          )}
          {pages.map((page) => (
            <div
              className={style.page}
              style={{ page: 'A4ProductCourse' }}
              key={`${page.day.delivery_date}-${page.section.shipper_name}-${page.pageInSection}`}
            >
              <div className={style.header}>
                <div className={style.title}>商品別コースリスト</div>
                <div className={style.sub}>
                  {page.section.shipper_name}{IDEOGRAPHIC_SPACE}{formatJapaneseDate(page.day.delivery_date)}
                </div>
                <div className={style.meta}>
                  発行日付: {issueDate}<br />
                  発行時刻: {issueTime}<br />
                  ページ: {page.pageInSection} / {page.totalInSection}
                </div>
              </div>
              <table className={style.table}>
                <thead>
                  <tr>
                    <th style={{ width: '11%' }}>商品CD<br />/ コース</th>
                    <th style={{ width: '24%' }}>商品名<br />/ 担当者</th>
                    <th style={{ width: '5%' }}>数量</th>
                    <th style={{ width: '6%' }}>荷姿</th>
                    <th style={{ width: '12%' }}>地区</th>
                    <th style={{ width: '34%' }}>納品先名</th>
                    <th style={{ width: '8%' }}>配送件数</th>
                  </tr>
                </thead>
                <tbody>
                  {page.rows.map((row, i) => {
                    if (row.kind === 'group') {
                      const g = row.group;
                      return (
                        <tr key={i} className={style.groupRow}>
                          <td className={style.center}>
                            <strong>{g.product_code}</strong>
                          </td>
                          <td>
                            <strong>{g.product_name}</strong>
                          </td>
                          <td className={style.num}>
                            <strong>{g.total_quantity}</strong>
                          </td>
                          <td className={style.center}>{packingSummary(g)}</td>
                          <td colSpan={2} />
                          <td className={style.center}>
                            <strong>[ {g.delivery_count} 件 ]</strong>
                          </td>
                        </tr>
                      );
                    }
                    const line = row.group.lines[row.lineIndex];
                    return (
                      <tr key={i}>
                        <td className={style.num}>{formatCourseLabel(line, page.section.show_rotation)}</td>
                        <td>{line.driver_name}</td>
                        <td className={style.num}>{line.quantity}</td>
                        <td className={style.center}>{line.packing_style}</td>
                        <td className={style.areaCell}>{line.area}</td>
                        {/* 明細行は配送件数欄を使わないため、納品先名で 2 列分を埋めて文字切れを軽減する */}
                        <td colSpan={2}>{line.store_name}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </>
  );
});

export default ProductCourseReportPresenter;
